/**
 * SoulumoAuthPage — the on-brand sign-up / sign-in door for
 * soulumo.app. The generic Reclaim /register page is Reclaim-branded
 * (cream background, Reclaim logo) and completely breaks the Soulumo
 * illusion when a first-time visitor taps Begin.
 *
 * Mar 5 2026 · Built as a single page with a mode toggle (join / sign
 * in) so the visitor never leaves Soulumo's aesthetic. On success we
 * route straight to /my-installations — the three-route chooser.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import SoulumoWord from "../../components/SoulumoWord";
import InstallOrb from "../../components/InstallOrb";
import { getVia } from "../../lib/via";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  bg: "radial-gradient(ellipse at 50% 15%, #131f38 0%, #0a132a 42%, #030710 88%)",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
  fieldBg: "rgba(245,236,217,0.04)",
  fieldBorder: "rgba(224,169,122,0.32)",
};

export default function SoulumoAuthPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login, register, user, loading: authLoading } = useAuth();
  // Mar 6 2026 · honour ?mode=signin from the header nav so users
  // who explicitly tap "Sign in" don't land on the join form.
  const [mode, setMode] = useState(sp.get("mode") === "signin" ? "signin" : "join");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(sp.get("code") || "");
  const [invitedBy, setInvitedBy] = useState(null);
  useEffect(() => {
    const v = getVia();
    if (v) axios.get(`${API}/api/with/${v}`).then((r) => r.data.dest === "soulumo" && setInvitedBy(r.data.name)).catch(() => {});
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isJoin = mode === "join";
  const [signupOpen, setSignupOpen] = useState(false);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/config`).then((r) => r.json()).then((c) => setSignupOpen(!!c.soulumo_signup_open)).catch(() => {});
  }, []);
  const rawNext = sp.get("next") || "";
  const dest = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/my-installations";

  // Already signed in — go straight into the app.
  useEffect(() => {
    if (!authLoading && user) nav(dest, { replace: true });
  }, [authLoading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isJoin) {
        // Mar 2026 · Soulumo is in private beta — testers either need
        // their email on the allow-list OR a valid shared invite code.
        // Try the shared-code path first so one-off testers who paste
        // the code from WhatsApp don't need Michael to pre-add them.
        const trimmedCode = inviteCode.trim();
        if (trimmedCode) {
          try {
            await axios.post(`${API}/api/beta-codes/claim`, {
              code: trimmedCode,
              email: email.trim().toLowerCase(),
              brand: "soulumo",
            });
          } catch (claimErr) {
            const d = claimErr.response?.data?.detail || "";
            if (/invalid_code/i.test(d)) {
              setError("That invite code isn't recognised. Please check it and try again.");
              setLoading(false);
              return;
            }
            // Other errors — fall through to register and let it
            // decide (the email may already be on the list).
          }
        }
        await register(name.trim(), email.trim().toLowerCase(), password);
      } else {
        await login(email.trim().toLowerCase(), password);
      }
      nav(dest);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      const status = err.response?.status;
      // Existing-account detection — the register endpoint returns
      // 400 "Email already registered" when a user tries to sign up
      // with an email that already exists. Auto-switch them to
      // sign-in mode instead of showing a scary "could not create
      // account" message they might read as a wrong-password error.
      if (isJoin && (status === 400 || status === 409) && /already/i.test(detail)) {
        setMode("signin");
        setError("You already have a Soulumo account. Sign in with your password below.");
      } else if (!isJoin && (status === 400 || status === 401)) {
        setError("That password doesn't match. Try again — or reset it if you've forgotten.");
      } else if (isJoin && /soulumo_beta_closed/i.test(detail)) {
        setError("Soulumo is opening soon. Enter your invite code above to join now.");
      } else {
        setError(detail || (isJoin ? "Could not create account." : "Could not sign in."));
      }
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    background: TOK.fieldBg,
    border: `1px solid ${TOK.fieldBorder}`,
    color: TOK.cream,
    fontFamily: '"Manrope", sans-serif',
    fontSize: "0.98rem",
    letterSpacing: "0.01em",
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden flex flex-col"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="soulumo-auth-page"
    >
      {/* Breathing halo */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: "-14%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 560,
          height: 560,
          background:
            "radial-gradient(circle, rgba(224,169,122,0.24) 0%, rgba(224,169,122,0.06) 30%, transparent 65%)",
          filter: "blur(30px)",
        }}
        aria-hidden
      />

      {/* Top bar — back + brand */}
      <div className="relative z-10 max-w-md mx-auto w-full px-6 pt-6 flex items-center justify-between">
        <button
          onClick={() => nav("/soulumo")}
          data-testid="soulumo-auth-back"
          className="text-[0.62rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          ← Back
        </button>
        <SoulumoWord size="xs" />
        <div style={{ width: 40 }} />
      </div>

      {/* Hero — small orb + tagline */}
      <div className="relative z-10 max-w-md mx-auto w-full px-6 pt-8 pb-4 text-center">
        <div style={{ width: 120, height: 120, margin: "0 auto" }}>
          <InstallOrb state="resting" size={120} />
        </div>
        <p
          className="mt-5 leading-tight"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.3rem, 4vw, 1.7rem)",
            color: TOK.cream,
            fontWeight: 300,
          }}
        >
          {isJoin ? (
            <>Begin your first <span style={{ fontStyle: "italic", color: TOK.gold }}>installation.</span></>
          ) : (
            <>Welcome <span style={{ fontStyle: "italic", color: TOK.gold }}>back.</span></>
          )}
        </p>
        <p
          className="mt-3 italic text-[0.95rem] leading-relaxed"
          style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
        >
          {isJoin
            ? "One quiet account. Your words, your voice, your design."
            : "Sign in to return to your installations."}
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={submit}
        className="relative z-10 max-w-md mx-auto w-full px-6 pt-4 pb-6 space-y-4"
        data-testid="soulumo-auth-form"
      >
        {isJoin && (
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            data-testid="soulumo-auth-name"
            className="w-full rounded-full px-5 py-3 focus:outline-none placeholder:text-[rgba(245,236,217,0.35)]"
            style={fieldStyle}
            autoComplete="name"
          />
        )}
        {isJoin && invitedBy && (
          <p className="px-5 text-sm italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }} data-testid="soulumo-auth-invited-by">
            Invited by {invitedBy}. No code needed.
          </p>
        )}
        {isJoin && !invitedBy && !signupOpen && (
          <div>
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="Invite code (if you have one)"
              data-testid="soulumo-auth-invite-code"
              className="w-full rounded-full px-5 py-3 focus:outline-none placeholder:text-[rgba(245,236,217,0.35)] tracking-[0.08em]"
              style={fieldStyle}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <p
              className="mt-1.5 px-5 text-[0.68rem] italic"
              style={{
                color: TOK.creamMist,
                fontFamily: '"Cormorant Garamond", serif',
              }}
              data-testid="soulumo-auth-invite-hint"
            >
              Soulumo is opening soon. If you were given an invite code, enter it here.
            </p>
          </div>
        )}
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          data-testid="soulumo-auth-email"
          className="w-full rounded-full px-5 py-3 focus:outline-none placeholder:text-[rgba(245,236,217,0.35)]"
          style={fieldStyle}
          autoComplete="email"
          inputMode="email"
        />
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isJoin ? "Create a password" : "Your password"}
            data-testid="soulumo-auth-password"
            className="w-full rounded-full px-5 py-3 pr-16 focus:outline-none placeholder:text-[rgba(245,236,217,0.35)]"
            style={fieldStyle}
            autoComplete={isJoin ? "new-password" : "current-password"}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-5 top-1/2 -translate-y-1/2 text-[0.6rem] uppercase tracking-[0.24em]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            data-testid="soulumo-auth-toggle-password"
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>

        {error && (
          <div
            className="rounded-xl px-4 py-3 text-center text-[0.85rem] italic"
            style={{
              background: "rgba(224,169,122,0.08)",
              border: "1px solid rgba(224,169,122,0.24)",
              color: TOK.gold,
              fontFamily: '"Cormorant Garamond", serif',
            }}
            data-testid="soulumo-auth-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="soulumo-auth-submit"
          className="w-full rounded-full px-7 py-3.5 disabled:opacity-60 transition-opacity"
          style={{
            background: TOK.gold,
            color: "#0a132a",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
          }}
        >
          {loading
            ? (isJoin ? "Creating…" : "Signing in…")
            : (isJoin ? "· Begin ·" : "· Sign in ·")}
        </button>
        {isJoin && (
          <p className="text-center text-xs leading-relaxed" style={{ color: "rgba(245,224,173,0.6)", fontFamily: '"Manrope", sans-serif' }} data-testid="soulumo-auth-consent">
            By creating an account you confirm you're 18 or over and agree to our{" "}
            <a href="/soulumo/terms" target="_blank" rel="noreferrer" className="underline">Terms</a> and <a href="/soulumo/privacy" target="_blank" rel="noreferrer" className="underline">Privacy policy</a>.
            Soulumo is for wellbeing, not medical advice. Never listen while driving.
          </p>
        )}
      </form>

      {/* Mode toggle + forgot-password */}
      <div className="relative z-10 max-w-md mx-auto w-full px-6 pb-10 text-center flex flex-col items-center gap-3.5">
        {!isJoin && (
          <button
            type="button"
            onClick={() => nav("/forgot-password")}
            data-testid="soulumo-auth-forgot-password"
            className="text-[0.72rem] italic"
            style={{
              color: TOK.goldSoft,
              fontFamily: '"Cormorant Garamond", serif',
              textDecoration: "underline",
              textUnderlineOffset: "3px",
            }}
          >
            Forgot your password?
          </button>
        )}
        <button
          type="button"
          onClick={() => { setMode(isJoin ? "signin" : "join"); setError(""); }}
          data-testid="soulumo-auth-mode-toggle"
          className="text-[0.66rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
        >
          {isJoin ? "Already have an account? Sign in →" : "New to Soulumo? Create an account →"}
        </button>
      </div>

      {/* Footer */}
      <footer
        className="relative z-10 max-w-md mx-auto w-full px-6 pb-8 text-center text-[0.55rem] uppercase tracking-[0.32em] mt-auto"
        style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
      >
        Soulumo · soulumo.app
      </footer>
    </div>
  );
}
